import { Observation } from "@medplum/fhirtypes";

import { normalizeObservationFormCode } from "@/encounters/components/EncounterLabor/EncounterLaborVersionDiff/structuredMedTherapyDiffConfig";
import {
  getAdmissionDischargeRowValues,
  getAssessmentNoteRowValues,
  getBirthingRowValues,
  getCervicalExamRowValues,
  getContractionsRowValues,
  getEstimatedBloodLossRowValues,
  getFHRRowValues,
  getInputOutputRowValues,
  getLaborVitalsRowValues,
  getLocationRowValues,
  getMedsTherapiesRowValues,
  getMembranesRowValues,
  getPositionRowValues,
  getResuscitationValues,
  getROMRowValues,
  getShoulderDystociaRowValues,
} from "@/encounters/components/EncounterLabor/utils/labor";
import {
  getApgarRowValues,
  getClientPositionRowValues,
  getCordClampedRowValues,
  getFirstLatchRowValues,
  getInitialIppAssessmentRowValues,
  getIPPVitalsRowValues,
  getNewbornFeedingRowValues,
  getNewbornInitialAssessmentRowValues,
  getNewbornLocationRowValues,
  getNewbornVitalsRowValues,
  getPerineumRowValues,
  getPlacentaRowValues,
  getAssessmentNoteRowValues as getPostpartumAssessmentNoteRowValues,
  getPostpartumHemorrhageRowValues,
  getPostpartumMedsTherapiesRowValues,
} from "@/encounters/components/EncounterLabor/utils/postpartum";

/**
 * Extracts plain display values from a labor or postpartum flowsheet observation for version diff.
 *
 * @param observation - Current or previous flowsheet observation row.
 * @returns Field map used by amend diff rendering and change detection.
 */
export function getLaborObservationDisplayValuesForVersionDiff(
  observation: Observation | undefined,
) {
  if (!observation) {
    return {};
  }

  const code = observation.code?.coding?.[0]?.code;
  const formName = normalizeObservationFormCode(code) ?? code;

  switch (formName) {
    case "fetal_heart_rate":
    case "55283-6":
      return getFHRRowValues(observation);
    case "labor_vitals":
    case "8716-3":
      return getLaborVitalsRowValues(observation);
    case "contractions":
      return getContractionsRowValues(observation);
    case "cervical_exam":
    case "cervical-exam":
      return getCervicalExamRowValues(observation);
    case "rupture_of_membranes":
    case "rom":
      return getROMRowValues(observation);
    case "position":
      return getPositionRowValues(observation);
    case "assessment_note":
    case "assessment-note":
      return getAssessmentNoteRowValues(observation);
    case "birthing":
      return getBirthingRowValues(observation);
    case "inputOutput":
    case "input-output":
      return getInputOutputRowValues(observation);
    case "membranes":
      return getMembranesRowValues(observation);
    case "medsAndTherapies":
    case "meds-therapies":
      return getMedsTherapiesRowValues(observation);
    case "newLocation":
    case "location":
      return getLocationRowValues(observation);
    case "shoulderDystocia":
      return getShoulderDystociaRowValues(observation);
    case "admitDischarge":
    case "admissionDischarge":
      return getAdmissionDischargeRowValues(observation);
    case "estimatedBloodLoss":
      return getEstimatedBloodLossRowValues(observation);
    case "transport": {
      const type = observation.component?.find((component) =>
        component.code?.coding?.[0]?.code?.startsWith("transport-type"),
      )?.code?.coding?.[0]?.display;
      const contact = observation.component?.find(
        (component) => component.code?.coding?.[0]?.code === "transport-contact",
      )?.valueString;
      const transportingFrom = observation.component?.find(
        (component) => component.code?.coding?.[0]?.code === "transport-transporting-from",
      )?.valueString;
      const hospitalName = observation.component?.find(
        (component) => component.code?.coding?.[0]?.code === "transport-hospital-name",
      )?.valueString;
      const condition = observation.component?.find(
        (component) => component.code?.coding?.[0]?.code === "transport-condition",
      )?.valueCodeableConcept?.coding?.[0]?.display;
      const notes = observation.note?.[0]?.text;

      return {
        type,
        contact,
        transportingFrom,
        hospitalName,
        condition,
        notes,
      };
    }
    case "resuscitation":
      return getResuscitationValues(observation);
    case "postpartumHemorrhage":
      return getPostpartumHemorrhageRowValues(observation);
    case "client_ipp_vitals":
      return getIPPVitalsRowValues(observation);
    case "client_placenta":
      return getPlacentaRowValues(observation);
    case "client_position":
      return getClientPositionRowValues(observation);
    case "client_cord_cut_clamped":
      return getCordClampedRowValues(observation);
    case "client_first_latch":
      return getFirstLatchRowValues(observation);
    case "client_assessment_note":
      return getPostpartumAssessmentNoteRowValues(observation);
    case "client_initial_ipp_assessment":
      return getInitialIppAssessmentRowValues(observation);
    case "client_full_ipp_assessment":
      return getPostpartumAssessmentNoteRowValues(observation);
    case "client_meds_therapies":
      return getPostpartumMedsTherapiesRowValues(observation);
    case "client_input_output":
      return getInputOutputRowValues(observation);
    case "client_perineum":
      return getPerineumRowValues(observation);
    case "newborn_vitals":
      return getNewbornVitalsRowValues(observation);
    case "newborn_cord_cut_clamped":
      return getCordClampedRowValues(observation);
    case "newborn_first_latch":
      return getFirstLatchRowValues(observation);
    case "newborn_apgar":
      return getApgarRowValues(observation);
    case "newborn_assessment_note":
      return getPostpartumAssessmentNoteRowValues(observation);
    case "newborn_initial_assessment":
      return getNewbornInitialAssessmentRowValues(observation);
    case "newborn_full_assessment":
      return getPostpartumAssessmentNoteRowValues(observation);
    case "newborn_physical_exam":
      return getPostpartumAssessmentNoteRowValues(observation);
    case "newborn_feeding":
      return getNewbornFeedingRowValues(observation);
    case "newborn_meds_therapies":
    case "newborn_meds_and_therapies":
      return getPostpartumMedsTherapiesRowValues(observation);
    case "newborn_location":
      return getNewbornLocationRowValues(observation);
    default:
      return { notes: observation.note?.[0]?.text };
  }
}
